import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';

import ProgressBarFull from '@components/ProgressBar';
import routes from '@components/routes';
import SidebarDropdown from '@components/Sidebar/SidebarDropdown';
import SidebarListItem from '@components/Sidebar/SidebarListItem';
import { routeEnabledForModifier, calculateProgressbar } from '@modules/app';
import { getIncompleteRoutes } from '@modules/nav';
import _svg from '@modules/svg';
import ContactUsModal from '@views/Public/Modals/ContactUs';
import withRouter from '@wrappers/withRouter';

const { VITE_ENV } = import.meta.env;

const Sidebar = props => {
	const { location } = props,
		app_id = useSelector(state => state.user.app_id),
		email = useSelector(state => state.user.email),
		support_student_id = useSelector(state => state.user.support_student_id),
		force_pwd_reset = useSelector(state => state.user.force_pwd_reset),
		app = useSelector(state => {
			const { app_id, apps, temp_app_rec } = state.user;
			return (app_id && apps[app_id] ? apps[app_id] : temp_app_rec).json_obj;
		}),
		[showModal, setShowModal] = useState(false),
		later = useMemo(() => !!location.state?.later, [location.state]),
		{ title: app_title } = app?.initial_information?.chosen_campus || {},
		this_route = routes.find(x => x.path === location.pathname),
		hideOnPaymentPage = location.pathname === '/payment' && later,
		percentage = app_id ? calculateProgressbar(app) : 0,
		headerText = getHeader();

	function activeRoute(routePath) {
		return location.pathname.indexOf(routePath) > -1 ? 'active' : '';
	}

	function completedScreen(routePath) {
		let { screensCompleted } = app,
			route = routes.find(r => r.path === routePath),
			{ completedBoolean, submitBoolean } = route || {},
			val = false;

		if (completedBoolean) {
			val = screensCompleted[completedBoolean];
		} else if (submitBoolean) {
			val = screensCompleted[submitBoolean];
		}

		return val;
	}

	function getHeader() {
		let str = '';

		if (support_student_id) str += 'Support Mode';
		if (app.date_submitted) str += ' Read Only';

		if (VITE_ENV !== 'production') str = `${str} ${VITE_ENV === 'practice' ? 'PRACTICE' : 'DEVELOPMENT'}`;

		return str;
	}

	function getAllChildRoutes(routePath) {
		const submit_paths = routes
			.filter(r => !!r.submitBoolean)
			.map(r => ({ path: r.path, submitBoolean: r.submitBoolean }));

		return routes
			.filter(r => !!r.childOf && r.childOf === routePath && routeEnabledForModifier(r.path, app))
			.map(r => {
				let blocked = routePath === '/submit' && !!r.submitBoolean && !!getIncompleteRoutes(app).length,
					i = submit_paths.findIndex(_r => _r.path === r.path);

				// tms:  submit pages can't rely on getIncompleteRoutes; just look at completed value of previous page
				if (!blocked && i > 0) blocked = !app.screensCompleted[submit_paths[i - 1].submitBoolean];

				return (
					<SidebarListItem
						key={r.path}
						name={r.name}
						routePath={r.path}
						disabledRoute={blocked}
						extraClass={activeRoute(r.path)}
						completed={completedScreen(r.path)}
						type='standalone'
						hideSpacer={true}
					/>
				);
			});
	}

	function getCurrentRouteSidebar() {
		const this_route = routes.find(x => x.path === location.pathname),
			{ loggedInRoutes, preAuthRoutes } = this_route || {};

		let returnRoutes = [];

		if (this_route && (loggedInRoutes || preAuthRoutes)) {
			let _loggedInRoutes = loggedInRoutes || [],
				_preAuthRoutes = preAuthRoutes || [];

			routes.forEach(route => {
				let useRoutes = email || !_preAuthRoutes.length ? _loggedInRoutes : _preAuthRoutes;

				if ((email && route.path === '/sign-out') || (app_id && route.path === '/save-and-return-later')) {
					returnRoutes.push(route);
				} else if (useRoutes.includes(route.path) && routeEnabledForModifier(route.path, app)) {
					if (route.childOf) {
						return;
					} else {
						returnRoutes.push(route);
					}
				}
			});
		}

		return returnRoutes;
	}

	function renderSimpleSidebar() {
		return (
			<div id='sidebar' className='sidebar homepageSidebar'>
				<div className='sidebar-wrapper'>
					<div className='sidebarHeader'>
						<img className='sidebarLogo' src={_svg.UWS_white} alt='UWs Logo' />
						{!!headerText && <h4 className='white italic'>{headerText}</h4>}
					</div>

					<ul className='nav'>
						{getCurrentRouteSidebar().map(prop => {
							if (prop.name === 'Direct Admit') {
								return (
									<SidebarDropdown
										routePath={prop.path}
										name='Direct Admit'
										submenuOpen={true}
										imgLogo={_svg.MyAccount}
										extraClass={activeRoute(prop.path)}
										hideSpacer={true}
										key={prop.path}>
										<ul className='ulnav'>
											<SidebarListItem
												key={`${prop.path}-docs`}
												img={_svg.AccountInformation}
												name='More Information'
												routePath='https://www.wisconsin.edu/direct-admissions/'
												hideSpacer={true}
											/>
										</ul>
									</SidebarDropdown>
								);
							}

							return (
								<SidebarListItem
									key={prop.path}
									name={prop.name}
									routePath={prop.path}
									completed={completedScreen(prop.path)}
									extraClass={activeRoute(prop.path)}
									type='standalone'
									hideSpacer={true}
								/>
							);
						})}
					</ul>
				</div>
			</div>
		);
	}

	if (force_pwd_reset || hideOnPaymentPage) return null;

	if (simpleSidebarPages.includes(location.pathname)) return renderSimpleSidebar();

	return (
		<div id='sidebar' className='sidebar'>
			<ContactUsModal show={showModal} closeModal={() => setShowModal(false)} />

			<div className='sidebar-wrapper'>
				<div className='sidebarHeader'>
					<img className='sidebarLogo' src={_svg.UWS_white} alt='UWs Logo' />
					{!!headerText && <h4 className='white italic'>{headerText}</h4>}
				</div>

				{this_route && 'enabledModifiers' in this_route && app_title && (
					<h3>{app.application_modifier === 'DA' ? 'Direct Admit Wisconsin' : app_title}</h3>
				)}

				<ul className='nav'>
					{getCurrentRouteSidebar().map((prop, index, key) => {
						//SKIP ANY ON RAMP IF READ ONLY
						if (
							['/select-modifier', '/select-campus', '/course-of-study', '/select-semester'].includes(
								prop.path
							) &&
							!!app.date_submitted
						) {
							return null;
						}

						//DROPDOWN ROUTES
						if (prop.parentDropdown) {
							let navBorder = false,
								childrenElements = <ul className='ulnav'>{getAllChildRoutes(prop.path)}</ul>;

							return (
								<div key={prop.path}>
									<SidebarDropdown
										routePath={prop.path}
										name={prop.name}
										submenuOpen={app.submenus[prop.submenuName]}
										submenuName={prop.submenuName}
										extraClass={activeRoute(prop.path)}
										completed={completedScreen(prop.path)}
										navBorder={navBorder}
										type='standalone'
										key={prop.path}>
										{childrenElements}
									</SidebarDropdown>
								</div>
							);
						}

						if (prop.name === 'My Account') {
							return (
								<SidebarDropdown
									routePath={prop.path}
									name='My Account'
									submenuOpen={location.pathname === '/my-account'}
									imgLogo={_svg.MyAccount}
									submenuName='my_account_open'
									extraClass={activeRoute(prop.path)}
									hideSpacer={true}
									key={prop.path}>
									<ul className='ulnav'>
										<SidebarListItem
											key={`${prop.path}-info`}
											img={_svg.AccountInformation}
											name='Account Information'
											routePath='/account'
											extraClass={activeRoute('/account')}
											hideSpacer={true}
										/>
										<SidebarListItem
											key='reciprocity'
											img={_svg.Reciprocity}
											name='Minnesota Reciprocity'
											routePath='/reciprocity'
											extraClass={activeRoute('/reciprocity')}
											hideSpacer={true}
										/>
									</ul>
								</SidebarDropdown>
							);
						}

						if (prop.name === 'Direct Admit') {
							return (
								<SidebarDropdown
									routePath={prop.path}
									name='Direct Admit'
									submenuOpen={true}
									imgLogo={_svg.MyAccount}
									extraClass={activeRoute(prop.path)}
									hideSpacer={true}
									key={prop.path}>
									<ul className='ulnav'>
										<SidebarListItem
											key={`${prop.path}-docs`}
											img={_svg.AccountInformation}
											name='More Information'
											routePath='https://www.wisconsin.edu/direct-admissions/'
											hideSpacer={true}
										/>
									</ul>
								</SidebarDropdown>
							);
						}

						if (prop.name === 'Contact Us') {
							return (
								<li onClick={() => setShowModal(true)} key={`sakey ${prop.name}`}>
									<div className='nav-link'>
										<img
											alt='Help nav link'
											className='sidebarIcon'
											height='15px'
											width='15px'
											src={_svg.ContactUs}
										/>
										<p className='sidebarRouteName'>{prop.name}</p>
									</div>
								</li>
							);
						}

						let route_name = `${prop.name}`;
						if (route_name === 'Direct Admit - Select Campus') route_name = 'Select Campuses';

						return (
							<SidebarListItem
								key={prop.path}
								name={route_name}
								routePath={prop.path}
								completed={completedScreen(prop.path)}
								extraClass={activeRoute(prop.path)}
								type='standalone'
							/>
						);
					})}
				</ul>
			</div>

			{this_route && this_route.showProgressBar && <ProgressBarFull percentage={percentage} />}
		</div>
	);
};

const simpleSidebarPages = [
	'/',
	'/mn-reciprocity',
	'/direct-admit',
	'/sign-in',
	'/create-account',
	'/reset-password',
	'/reset-password-form',
	'/link-direct-admit'
];

export default withRouter(Sidebar);
